// EngagementsSection.jsx
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Chip,
  Tooltip as MuiTooltip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Logger } from '../../utils/logger';
import {
  BG_BASE, BG_SURFACE,
  BRAND_PURPLE, BRAND_ORANGE,
  COLOR_GOOD, COLOR_WARN, COLOR_POOR,
  BORDER_CARD,
} from '../../utils/colors';
import ProjectNameLink from '../common/ProjectNameLink';
import ClientNameLink from '../common/ClientNameLink';

const logger = new Logger('EngagementsSection');

const EngagementsSection = ({ engagementData = {} }) => {
  const {
    current_engagements = [],
    past_engagements = [],
    project_history = [],
    role_distribution = {}
  } = engagementData;

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };


  const getCsatColor = (score) => {
    if (score >= 4.5) return COLOR_GOOD;
    if (score >= 4.0) return BRAND_PURPLE;
    if (score >= 3.5) return COLOR_WARN;
    return COLOR_POOR;
  };

  // Combine current and past engagements and project history for timeline
  const allEngagements = [...current_engagements, ...past_engagements, ...project_history].sort((a, b) => {
    // Sort by start date most recent first
    const dateA = a.start_date || a.role_start_date || a.project_start_date;
    const dateB = b.start_date || b.role_start_date || b.project_start_date;
    return new Date(dateB) - new Date(dateA);
  });

  // Debug logging
  logger.debug("Engagement data received:", {
    current_count: current_engagements.length,
    past_count: past_engagements.length,
    project_history_count: project_history.length,
    total: allEngagements.length
  });

  // Count roles from all engagements (consistent with industry distribution count)
  const roleDistributionData = useMemo(() => {
    const map = {};
    allEngagements.forEach(e => {
      const role = e.role || 'Unknown';
      if (!map[role]) map[role] = 0;
      map[role]++;
    });
    return Object.entries(map)
      .map(([name, projectCount]) => ({ name, projectCount }))
      .sort((a, b) => b.projectCount - a.projectCount);
  }, [allEngagements]);
  const ROLE_COLORS = [COLOR_GOOD, BRAND_PURPLE, COLOR_WARN, BRAND_ORANGE, COLOR_POOR];

  const [industryView, setIndustryView] = useState('projects');

  const industryDistributionData = useMemo(() => {
    const map = {};
    allEngagements.forEach(e => {
      const industry = e.client_industry || 'Unknown';
      if (!map[industry]) map[industry] = { projectCount: 0, clients: new Set() };
      map[industry].projectCount++;
      if (e.client_id) map[industry].clients.add(e.client_id);
    });
    return Object.entries(map)
      .map(([name, d]) => ({
        name,
        count: industryView === 'projects' ? d.projectCount : d.clients.size,
      }))
      .sort((a, b) => b.count - a.count);
  }, [allEngagements, industryView]);

  return (
    <Box sx={{ p: 2, bgcolor: BG_SURFACE, borderRadius: 1 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Engagements
      </Typography>

      <Grid container spacing={2}>
        {/* Project Timeline - Replaced Timeline with List */}
        <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: BG_BASE,
              border: `1px solid ${BORDER_CARD}`,
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Projects
              </Typography>
              <MuiTooltip title="Timeline of past and current projects">
                <IconButton size="small">
                  <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </MuiTooltip>
            </Box>

            {allEngagements.length > 0 ? (
              <Box sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Grid container sx={{ mb: 1, px: 2, fontWeight: 'bold', bgcolor: BG_SURFACE, position: 'sticky', top: 0, zIndex: 1 }}>
                  <Grid item xs={5}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Project
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Status
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Dates
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ position: 'sticky', top: '28px', zIndex: 1 }} />

                <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                {allEngagements.map((engagement, index) => {
                  // Determine dates - handle different field names
                  const startDate = engagement.start_date || engagement.role_start_date || engagement.project_start_date;
                  const endDate = engagement.end_date || engagement.role_end_date || engagement.project_end_date;

                  // Map a raw status string to display values
                  const toStatusDisplay = (raw) => {
                    const s = (raw || '').toLowerCase();
                    if (s.includes('soft')) return ['Soft Booked', BRAND_ORANGE, `${BRAND_ORANGE}26`];
                    if (s === 'completed' || s === 'complete' || s === 'closed' || s === 'cancelled' || s === 'canceled') return ['Complete', COLOR_WARN, `${COLOR_WARN}26`];
                    if (s === 'planned' || s === 'upcoming') return ['Planned', BRAND_PURPLE, `${BRAND_PURPLE}26`];
                    if (s === 'active' || s === 'ongoing' || s === 'in progress' || s === 'in_progress' || s === 'confirmed') return ['Active', COLOR_GOOD, `${COLOR_GOOD}26`];
                    return null;
                  };

                  let statusLabel, statusColor, statusBg;

                  // Prefer real project status injected by the backend from CLAUDE_PROJECT_BASIC
                  const projectStatusMatch = toStatusDisplay(engagement.project_status);
                  if (projectStatusMatch) {
                    [statusLabel, statusColor, statusBg] = projectStatusMatch;
                  } else {
                    // Fallback: derive from dates using proper Date parsing
                    const parseEngDate = (d) => {
                      if (!d) return null;
                      if (typeof d === 'object' && d.$date) return new Date(d.$date);
                      const parsed = new Date(d);
                      return isNaN(parsed.getTime()) ? null : parsed;
                    };
                    const SYSTEM_DATE = new Date('2024-08-26');
                    const parsedStart = parseEngDate(startDate);
                    const parsedEnd = parseEngDate(endDate);

                    const fallbackStatus = (engagement.assignment_status || engagement.status || '').toLowerCase();
                    const fallbackMatch = toStatusDisplay(fallbackStatus);
                    if (fallbackMatch) {
                      [statusLabel, statusColor, statusBg] = fallbackMatch;
                    } else if (parsedStart && parsedStart > SYSTEM_DATE) {
                      statusLabel = 'Planned'; statusColor = BRAND_PURPLE; statusBg = `${BRAND_PURPLE}26`;
                    } else if (parsedEnd && parsedEnd < SYSTEM_DATE) {
                      statusLabel = 'Complete'; statusColor = COLOR_WARN; statusBg = `${COLOR_WARN}26`;
                    } else {
                      statusLabel = 'Active'; statusColor = COLOR_GOOD; statusBg = `${COLOR_GOOD}26`;
                    }
                  }

                  return (
                    <React.Fragment key={index}>
                      <Grid container sx={{ py: 2, px: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)', alignItems: 'center' }}>
                        {/* Column 1: Project Name, Client and Role */}
                        <Grid item xs={5}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            <ProjectNameLink project={{ project_id: engagement.project_id, name: engagement.project_name || engagement.project, client_name: engagement.client_name }}>
                              {engagement.project_name || engagement.project || `Project ${engagement.project_id}` || 'N/A'}
                            </ProjectNameLink>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            <ClientNameLink client={{ client_id: engagement.client_id, client_name: engagement.client_name || engagement.client }}>
                              {engagement.client_name || engagement.client || engagement.client_id || 'N/A'}
                            </ClientNameLink>
                            {' • '}{engagement.role || 'N/A'}
                          </Typography>
                        </Grid>

                        {/* Column 2: Status */}
                        <Grid item xs={3}>
                          <Chip
                            label={statusLabel}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 'bold',
                              color: statusColor,
                              bgcolor: statusBg,
                              border: `1px solid ${statusColor}55`,
                            }}
                          />
                        </Grid>

                        {/* Column 3: Dates */}
                        <Grid item xs={4}>
                          <Typography variant="body2" sx={{ color: statusLabel === 'Active' ? COLOR_GOOD : COLOR_WARN }}>
                            {formatDate(startDate)} - {endDate ? formatDate(endDate) : 'Present'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </React.Fragment>
                  );
                })}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 5 }}>
                No engagement history available
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Role Distribution */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: BG_BASE,
              border: `1px solid ${BORDER_CARD}`,
              mb: 2
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Role Distribution
              </Typography>
              <MuiTooltip title="Distribution of projects by role">
                <IconButton size="small">
                  <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </MuiTooltip>
            </Box>

            {roleDistributionData.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {/* Primary Role - Moved to top */}
                {role_distribution.primary_role && (
                  <Typography variant="body1" align="center" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Primary Role: <Box component="span" sx={{ color: COLOR_GOOD }}>{role_distribution.primary_role}</Box>
                  </Typography>
                )}
                
                {/* Pie chart removed */}
                
                {(() => {
                  const topFiveRoles = [...roleDistributionData.slice(0, 5)];
                  const nextFiveRoles = [...roleDistributionData.slice(5, 10)];
                  while (topFiveRoles.length < 5) topFiveRoles.push(null);
                  while (nextFiveRoles.length < 5) nextFiveRoles.push(null);
                  
                  return (
                    <Grid container spacing={1}>
                      {/* Left column - Top 5 roles */}
                      <Grid item xs={6}>
                        {topFiveRoles.map((item, index) => (
                          item ? (
                            <Box key={index} sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                              <Box 
                                sx={{ 
                                  width: '100%', 
                                  height: 24, 
                                  bgcolor: ROLE_COLORS[index % ROLE_COLORS.length],
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  px: 1
                                }}
                              >
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 'bold', fontSize: '0.65rem' }}>
                                  {item.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 'bold', fontSize: '0.65rem' }}>
                                  {item.projectCount}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Box key={index} sx={{ mb: 1, height: 24 }} /> // Empty placeholder
                          )
                        ))}
                      </Grid>
                      
                      {/* Right column - Next 5 roles */}
                      <Grid item xs={6}>
                        {nextFiveRoles.map((item, index) => (
                          item ? (
                            <Box key={index} sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                              <Box 
                                sx={{ 
                                  width: '100%', 
                                  height: 24, 
                                  bgcolor: ROLE_COLORS[(index + 5) % ROLE_COLORS.length],
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  px: 1
                                }}
                              >
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 'bold', fontSize: '0.65rem' }}>
                                  {item.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 'bold', fontSize: '0.65rem' }}>
                                  {item.projectCount}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Box key={index} sx={{ mb: 1, height: 24 }} /> // Empty placeholder
                          )
                        ))}
                      </Grid>
                    </Grid>
                  );
                })()}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 5 }}>
                No role distribution data available
              </Typography>
            )}
          </Paper>

          {/* Industry Distribution */}
          <Paper
            elevation={0}
            sx={{ p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', mb: 2 }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Industry Distribution
                </Typography>
                <MuiTooltip title="Industries of clients this employee has worked with">
                  <IconButton size="small">
                    <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </MuiTooltip>
              </Box>
              <ToggleButtonGroup
                value={industryView}
                exclusive
                onChange={(_, v) => { if (v) setIndustryView(v); }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    color: 'text.secondary', borderColor: BORDER_CARD,
                    px: 1.5, fontSize: '0.7rem', textTransform: 'none', py: 0.5,
                  },
                  '& .MuiToggleButton-root.Mui-selected': {
                    color: COLOR_GOOD, borderColor: COLOR_GOOD,
                    bgcolor: `${COLOR_GOOD}1A`,
                    '&:hover': { bgcolor: `${COLOR_GOOD}26` },
                  },
                }}
              >
                <ToggleButton value="projects">Projects</ToggleButton>
                <ToggleButton value="clients">Clients</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {industryDistributionData.length > 0 ? (() => {
              const top = [...industryDistributionData.slice(0, 5)];
              const next = [...industryDistributionData.slice(5, 10)];
              while (top.length < 5) top.push(null);
              while (next.length < 5) next.push(null);
              return (
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    {top.map((item, i) => item ? (
                      <Box key={i} sx={{ mb: 1 }}>
                        <Box sx={{ width: '100%', height: 24, bgcolor: ROLE_COLORS[i % ROLE_COLORS.length],
                          borderRadius: 1, display: 'flex', alignItems: 'center',
                          justifyContent: 'space-between', px: 1 }}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.95)',
                            fontWeight: 'bold', fontSize: '0.65rem', overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>
                            {item.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.95)',
                            fontWeight: 'bold', fontSize: '0.65rem' }}>
                            {item.count}
                          </Typography>
                        </Box>
                      </Box>
                    ) : <Box key={i} sx={{ mb: 1, height: 24 }} />)}
                  </Grid>
                  <Grid item xs={6}>
                    {next.map((item, i) => item ? (
                      <Box key={i} sx={{ mb: 1 }}>
                        <Box sx={{ width: '100%', height: 24,
                          bgcolor: ROLE_COLORS[(i + 5) % ROLE_COLORS.length],
                          borderRadius: 1, display: 'flex', alignItems: 'center',
                          justifyContent: 'space-between', px: 1 }}>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.95)',
                            fontWeight: 'bold', fontSize: '0.65rem', overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>
                            {item.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.95)',
                            fontWeight: 'bold', fontSize: '0.65rem' }}>
                            {item.count}
                          </Typography>
                        </Box>
                      </Box>
                    ) : <Box key={i} sx={{ mb: 1, height: 24 }} />)}
                  </Grid>
                </Grid>
              );
            })() : (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                No industry data available
              </Typography>
            )}
          </Paper>

          {/* Team Dynamics */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Top Collaborators
              </Typography>
              <MuiTooltip title="Employees you've worked with on the most projects">
                <IconButton size="small">
                  <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </MuiTooltip>
            </Box>

            {engagementData.top_collaborators && engagementData.top_collaborators.length > 0 ? (
              <List sx={{ width: '100%' }}>
                {engagementData.top_collaborators.slice(0, 5).map((collaborator, index) => {
                  // Handle different data formats from backend
                  const name = collaborator.collaborator_name || collaborator.name || collaborator.employee_name || 'Unknown';
                  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();
                  const projectCount = collaborator.projects_together || collaborator.projects || 0;

                  return (
                    <ListItem key={index} sx={{ py: 1 }}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: ROLE_COLORS[index % ROLE_COLORS.length], width: 40, height: 40 }}>
                          {initials}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1">
                            {name}
                          </Typography>
                        }
                        secondary={
                          <React.Fragment>
                            <Typography variant="body2" color="text.secondary">
                              {collaborator.collaboration_strength || collaborator.role || 'Team Member'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {projectCount} {projectCount === 1 ? 'project' : 'projects'} worked together
                            </Typography>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 5 }}>
                No collaboration data available
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EngagementsSection;
